import type { AuthMode } from "@/domain/permissions";
import type { SupabaseConfig } from "./supabaseClient";

export interface AppConfig {
  authMode: AuthMode;
  /** Individual mode only; `null` when the deployment lacks the Supabase variables. */
  supabase: SupabaseConfig | null;
}

const SHARED: AppConfig = { authMode: "shared", supabase: null };

/**
 * Reads the deployment's settings from `/api/config` (Pages Function backed by
 * the `AUTH_MODE`, `SUPABASE_URL` and `SUPABASE_ANON_KEY` environment
 * variables). Falls back to shared mode when the endpoint is unreachable.
 */
export async function fetchAppConfig(fetchImpl: typeof fetch = fetch): Promise<AppConfig> {
  try {
    const response = await fetchImpl("/api/config", { cache: "no-store" });
    if (!response.ok) return SHARED;
    const config = (await response.json()) as {
      authMode?: unknown;
      supabaseUrl?: unknown;
      supabaseAnonKey?: unknown;
    };
    if (config.authMode !== "individual") return SHARED;
    const { supabaseUrl: url, supabaseAnonKey: anonKey } = config;
    return {
      authMode: "individual",
      supabase:
        typeof url === "string" && url && typeof anonKey === "string" && anonKey
          ? { url, anonKey }
          : null,
    };
  } catch {
    return SHARED;
  }
}
